import React, { useEffect, useState } from "react";
import { Order } from "../types";
import { orderService } from "../services/dataService";
import "../pages/PurchaseOrders.css";

interface Props {
  order: Order;
  onClose: () => void;
  onSaved: (invoice: string | null, invoiceNumber: string | null) => void;
}

/**
 * Elle fatura bilgisi girişi — YALNIZ fatura modülü lisansta yokken görünür.
 * Kağıt/harici kesilen faturanın numarası ve (isteğe bağlı) seri/açıklaması
 * siparişe işlenir. Modül varken numara belge servisinden gelir; sunucu bu ucu
 * 403 ile kapatır.
 */
const InvoiceNumberModal: React.FC<Props> = ({ order, onClose, onSaved }) => {
  const [invoice, setInvoice] = useState(order.invoice || "");
  const [invoiceNumber, setInvoiceNumber] = useState(order.invoiceNumber || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !saving) { e.stopPropagation(); onClose(); } };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose, saving]);

  const save = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const no = invoiceNumber.trim();
    if (!no) return setError("Fatura numarası boş olamaz.");
    setSaving(true);
    setError("");
    try {
      await orderService.updateInvoice(order.orderId, {
        invoice: invoice.trim() || null,
        invoiceNumber: no,
      });
      onSaved(invoice.trim() || null, no);
      onClose();
    } catch (err: any) {
      const d = err?.response?.data;
      setError(d?.message || (typeof d === "string" ? d : "Fatura bilgisi kaydedilemedi."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="po-overlay center top" onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <form className="po-modal" role="dialog" aria-modal="true" aria-labelledby="im-title" onSubmit={save}>
        <div className="po-modal-head">
          <div>
            <h3 id="im-title">Fatura no gir</h3>
            <div className="po-badges"><span className="po-badge muted">Elle giriş</span></div>
          </div>
          <button type="button" className="po-ib big" onClick={onClose} aria-label="Kapat" disabled={saving}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>

        <div className="po-modal-body">
          <dl className="po-facts">
            <div className="wide">
              <dt>Sipariş</dt>
              <dd>
                {order.firm?.firmName || "—"}
                <small>{order.model?.modelCode || "—"} · {order.quantity} {typeof order.unit === "string" ? order.unit : "adet"}</small>
              </dd>
            </div>
          </dl>

          {error && <div className="po-note err" role="alert">{error}</div>}

          <label className="po-field">
            <span>Fatura numarası <em>zorunlu</em></span>
            <input
              id="im-no"
              value={invoiceNumber}
              maxLength={50}
              placeholder="Örn. ABC2026000000123"
              autoFocus
              className={error && !invoiceNumber.trim() ? "is-invalid" : ""}
              onChange={(e) => { setInvoiceNumber(e.target.value); setError(""); }}
            />
          </label>
          <label className="po-field">
            <span>Seri / açıklama <em>isteğe bağlı</em></span>
            <input
              id="im-inv"
              value={invoice}
              maxLength={100}
              placeholder="Örn. ABC serisi, kağıt fatura"
              onChange={(e) => setInvoice(e.target.value)}
            />
          </label>
          <div className="po-note">
            Bu bilgi sistem dışında kesilmiş faturayı siparişle eşlemek içindir; belge
            üretilmez ve GİB'e gönderim yapılmaz.
          </div>
        </div>

        <div className="po-modal-foot between">
          <span className="po-hint">{order.invoiceNumber ? <>Mevcut: <b>{order.invoiceNumber}</b></> : "Henüz fatura no girilmemiş"}</span>
          <div className="po-foot-btns">
            <button type="button" className="po-btn" onClick={onClose} disabled={saving}>Vazgeç</button>
            <button type="submit" className="po-btn primary" disabled={saving}>
              {saving ? <><span className="po-spin-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M20 12a8 8 0 1 1-2.3-5.6" /></svg></span>Kaydediliyor…</> : "Kaydet"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default InvoiceNumberModal;
